import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Empty, InputNumber, Modal, Row, Space, Spin, Statistic, Switch, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';

import { fetchAuthSession } from '../auth/gateway';
import {
  DATE_QUERY_PARAM,
  DEFAULT_TRACKINTEL_DIST_THRESHOLD_M,
  DEFAULT_TRACKINTEL_GAP_THRESHOLD_MIN,
  DEFAULT_TRACKINTEL_MERGE_STAYPOINTS_ENABLED,
  DEFAULT_TRACKINTEL_MERGE_STAYPOINTS_MAX_TIME_GAP_MIN,
  DEFAULT_TRACKINTEL_TIME_THRESHOLD_MIN,
  JST_TIMEZONE,
  SNAPSHOT_PAGE_SIZE,
} from '../components/location-analysis/analysisConfig';
import {
  buildDayRange,
  buildRawGnssFetchRange,
  formatAccuracy,
  formatAltitude,
  formatDateTime,
  formatDistance,
  formatDurationSeconds,
  formatMeasuredSpeed,
  formatObservedExtentDistance,
  formatSelectedDayQueryParam,
  formatSpeed,
  isWithinRange,
  parseMeasuredAt,
  parseSelectedDayQueryParam,
} from '../components/location-analysis/dateUtils';
import LocationAnalysisMap from '../components/location-analysis/LocationAnalysisMap';
import LocationAnalysisTimeline from '../components/location-analysis/LocationAnalysisTimeline';
import { buildLocationAnalysisTimelineItems } from '../components/location-analysis/timelineBuilder';
import { getSemanticTypeLabel, getTriplegModeLabel, SemanticTypeIcon, TriplegModeIcon } from '../components/location-analysis/timelineFormatters';
import type {
  DerivedLocation,
  DerivedStaypoint,
  DerivedTimelineTarget,
  DerivedTrip,
  DerivedTripleg,
  POICandidate,
  POIEnrichment,
} from '../components/location-analysis/timelineTypes';
import { API_CONFIG } from '../config/api';
import { useDevice } from '../contexts/DeviceContext';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { secureLogger } from '../utils/secureLogger';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;

type RawGnssPoint = {
  id: string;
  measured_at: string;
  latitude: number;
  longitude: number;
  accuracy?: number | null;
  altitude?: number | null;
  speed?: number | null;
  is_interpolated?: boolean;
  interpolation_source_snapshot_ids?: string[] | null;
  analysis_exclusion_reason?: string | null;
};

type RawGnssTableRow = RawGnssPoint & {
  row_number: number;
  measured_at_label: string;
  previous_distance_m: number | null;
  previous_time_diff_seconds: number | null;
  previous_speed_kmh: number | null;
};

type RebuildStage = {
  name: string;
  status: string;
  [key: string]: unknown;
};

type TrackintelPositionfix = RawGnssPoint;

type TrackintelExcludedPositionfix = {
  snapshot_id: string;
  measured_at: string;
  latitude: number;
  longitude: number;
  accuracy: number;
  speed?: number | null;
  exclusion_reason: string;
};

type TrackintelStageDeviceRow = {
  device_id: string;
  positionfixes?: TrackintelPositionfix[];
  excluded_positionfixes?: TrackintelExcludedPositionfix[];
  [key: string]: unknown;
};

type RebuildResult = {
  deleted_staypoints: number;
  created_staypoints: number;
  deleted_triplegs?: number;
  created_triplegs?: number;
  deleted_trips?: number;
  created_trips?: number;
  matched_locations: number;
  created_locations: number;
  active_location_count: number;
  archived_location_count: number;
  candidate_stays?: number;
  candidate_triplegs?: number;
  candidate_trips?: number;
  filtered_outlier_count?: number;
  valhalla_matched_triplegs?: number;
  stages?: RebuildStage[];
};

type RebuildJob = {
  job_id: string;
  status: 'accepted' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  error_message?: string | null;
  result?: RebuildResult | null;
  trackintel_dist_threshold_m?: number | null;
  trackintel_time_threshold_min?: number | null;
  trackintel_gap_threshold_min?: number | null;
  trackintel_merge_staypoints_enabled?: boolean | null;
  trackintel_merge_staypoints_max_time_gap?: string | null;
};

type TrackintelRunParameters = {
  distThresholdM: number;
  timeThresholdMin: number;
  gapThresholdMin: number;
  mergeStaypointsEnabled: boolean;
  mergeStaypointsMaxTimeGapMin: number;
};

type GnssSnapshotListResponse = {
  snapshots: RawGnssPoint[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
};

const summarizeStageDetail = (record: RebuildStage) => {
  if (record.name !== 'trackintel_analyze') {
    return record;
  }

  const summarized = { ...record };
  const devices = Array.isArray(record.devices) ? (record.devices as TrackintelStageDeviceRow[]) : [];
  summarized.devices = devices.map((device) => {
    const positionfixCount = Array.isArray(device.positionfixes) ? device.positionfixes.length : 0;
    const excludedPositionfixCount = Array.isArray(device.excluded_positionfixes)
      ? device.excluded_positionfixes.length
      : 0;
    if (!Array.isArray(device.positionfixes)) {
      return device;
    }
    return {
      ...device,
      positionfixes: `[omitted ${positionfixCount} positionfixes]`,
      excluded_positionfixes: `[omitted ${excludedPositionfixCount} excluded positionfixes]`,
    };
  });
  return summarized;
};

const haversineMeters = (
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number
) => {
  const earthRadiusM = 6371000;
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const dLat = toRadians(toLat - fromLat);
  const dLng = toRadians(toLng - fromLng);
  const lat1 = toRadians(fromLat);
  const lat2 = toRadians(toLat);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadiusM * Math.asin(Math.sqrt(a));
};

type TrackintelControlsProps = {
  running: boolean;
  onRun: (parameters: TrackintelRunParameters) => void | Promise<void>;
};

const TrackintelControls = memo<TrackintelControlsProps>(({ running, onRun }) => {
  const [distThresholdM, setDistThresholdM] = useState<number>(DEFAULT_TRACKINTEL_DIST_THRESHOLD_M);
  const [timeThresholdMin, setTimeThresholdMin] = useState<number>(DEFAULT_TRACKINTEL_TIME_THRESHOLD_MIN);
  const [gapThresholdMin, setGapThresholdMin] = useState<number>(DEFAULT_TRACKINTEL_GAP_THRESHOLD_MIN);
  const [mergeStaypointsEnabled, setMergeStaypointsEnabled] = useState<boolean>(
    DEFAULT_TRACKINTEL_MERGE_STAYPOINTS_ENABLED
  );
  const [mergeStaypointsMaxTimeGapMin, setMergeStaypointsMaxTimeGapMin] = useState<number>(
    DEFAULT_TRACKINTEL_MERGE_STAYPOINTS_MAX_TIME_GAP_MIN
  );

  const handleRunClick = useCallback(() => {
    void onRun({
      distThresholdM,
      timeThresholdMin,
      gapThresholdMin,
      mergeStaypointsEnabled,
      mergeStaypointsMaxTimeGapMin,
    });
  }, [
    distThresholdM,
    gapThresholdMin,
    mergeStaypointsEnabled,
    mergeStaypointsMaxTimeGapMin,
    onRun,
    timeThresholdMin,
  ]);

  return (
    <Space wrap size={8}>
      <InputNumber
        addonBefore="距離閾値"
        addonAfter="m"
        min={1}
        size="small"
        value={distThresholdM}
        onChange={(value) => setDistThresholdM(value ?? DEFAULT_TRACKINTEL_DIST_THRESHOLD_M)}
      />
      <InputNumber
        addonBefore="時間閾値"
        addonAfter="min"
        min={1}
        size="small"
        value={timeThresholdMin}
        onChange={(value) => setTimeThresholdMin(value ?? DEFAULT_TRACKINTEL_TIME_THRESHOLD_MIN)}
      />
      <InputNumber
        addonBefore="gap閾値"
        addonAfter="min"
        min={1}
        size="small"
        value={gapThresholdMin}
        onChange={(value) => setGapThresholdMin(value ?? DEFAULT_TRACKINTEL_GAP_THRESHOLD_MIN)}
      />
      <Space size={4}>
        <Text>後段マージ</Text>
        <Switch checked={mergeStaypointsEnabled} onChange={setMergeStaypointsEnabled} />
      </Space>
      <InputNumber
        addonBefore="マージgap"
        addonAfter="min"
        min={0}
        size="small"
        disabled={!mergeStaypointsEnabled}
        value={mergeStaypointsMaxTimeGapMin}
        onChange={(value) =>
          setMergeStaypointsMaxTimeGapMin(value ?? DEFAULT_TRACKINTEL_MERGE_STAYPOINTS_MAX_TIME_GAP_MIN)
        }
      />
      <Button
        type="primary"
        icon={<PlayCircleOutlined />}
        loading={running}
        onClick={handleRunClick}
        size="small"
      >
        LocationAnalysis を実行
      </Button>
    </Space>
  );
});
TrackintelControls.displayName = 'TrackintelControls';

const LocationAnalysisPlaygroundPage: React.FC = () => {
  const { selectedDevice } = useDevice();
  const { setPageHeader } = usePageHeader();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDayQueryParam = searchParams.get(DATE_QUERY_PARAM);
  const [selectedDay, setSelectedDay] = useState<Dayjs>(() => {
    return parseSelectedDayQueryParam(selectedDayQueryParam) || dayjs().tz(JST_TIMEZONE);
  });
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [rawPoints, setRawPoints] = useState<RawGnssPoint[]>([]);
  const [selectedRawPointId, setSelectedRawPointId] = useState<string | null>(null);
  const [selectedDerivedTarget, setSelectedDerivedTarget] = useState<DerivedTimelineTarget | null>(null);
  const [locations, setLocations] = useState<DerivedLocation[]>([]);
  const [visibleLocations, setVisibleLocations] = useState<DerivedLocation[]>([]);
  const [staypoints, setStaypoints] = useState<DerivedStaypoint[]>([]);
  const [triplegs, setTriplegs] = useState<DerivedTripleg[]>([]);
  const [trips, setTrips] = useState<DerivedTrip[]>([]);
  const [poiCandidatesByLocationId, setPoiCandidatesByLocationId] = useState<Map<string, POICandidate[]>>(new Map());
  const [searchingPoiLocationId, setSearchingPoiLocationId] = useState<string | null>(null);
  const [linkingPoiLocationId, setLinkingPoiLocationId] = useState<string | null>(null);
  const [rebuildJob, setRebuildJob] = useState<RebuildJob | null>(null);
  const poiSearchCacheRef = useRef<Map<string, POICandidate[]>>(new Map());
  const poiSearchRequestIdByLocationRef = useRef<Map<string, number>>(new Map());

  const dayRange = useMemo(() => buildDayRange(selectedDay), [selectedDay]);
  const rawGnssFetchRange = useMemo(() => buildRawGnssFetchRange(selectedDay), [selectedDay]);

  const updateSelectedDayQueryParam = useCallback(
    (nextDay: Dayjs) => {
      const nextDateParam = formatSelectedDayQueryParam(nextDay);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(DATE_QUERY_PARAM, nextDateParam);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleSelectedDayChange = useCallback(
    (value: Dayjs | null) => {
      if (!value) {
        return;
      }
      const nextDay = value.tz(JST_TIMEZONE);
      setSelectedDay(nextDay);
      updateSelectedDayQueryParam(nextDay);
    },
    [updateSelectedDayQueryParam]
  );

  useEffect(() => {
    const queryDay = parseSelectedDayQueryParam(selectedDayQueryParam);
    if (!queryDay) {
      updateSelectedDayQueryParam(selectedDay);
      return;
    }
    const queryDateParam = formatSelectedDayQueryParam(queryDay);
    setSelectedDay((current) => {
      return formatSelectedDayQueryParam(current) === queryDateParam ? current : queryDay;
    });
  }, [selectedDay, selectedDayQueryParam, updateSelectedDayQueryParam]);

  const fetchToken = useCallback(async () => {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (!token) {
      throw new Error('認証トークンが取得できませんでした');
    }
    return token;
  }, []);

  const fetchAllRawGnss = useCallback(
    async (token: string, deviceId: string) => {
      const snapshots: RawGnssPoint[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const params = new URLSearchParams({
          from_time: rawGnssFetchRange.startIso,
          to_time: rawGnssFetchRange.endIso,
          limit: `${SNAPSHOT_PAGE_SIZE}`,
          offset: `${offset}`,
        });
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${deviceId}/gnss/snapshots?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`GNSS snapshots API failed: ${response.status}`);
        }
        const payload = (await response.json()) as GnssSnapshotListResponse;
        snapshots.push(...payload.snapshots);
        hasMore = payload.has_more;
        offset += payload.limit;
      }

      return snapshots;
    },
    [rawGnssFetchRange.endIso, rawGnssFetchRange.startIso]
  );

  const fetchDerived = useCallback(
    async (token: string, deviceId: string) => {
      const params = new URLSearchParams({
        range_start: rawGnssFetchRange.startIso,
        range_end: rawGnssFetchRange.endIso,
        device_id: deviceId,
      });
      const [locationsResponse, staypointsResponse, triplegsResponse, tripsResponse] = await Promise.all([
        fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/locations?include_inactive=true`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/staypoints?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/triplegs?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/trips?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!locationsResponse.ok) {
        throw new Error(`locations API failed: ${locationsResponse.status}`);
      }
      if (!staypointsResponse.ok) {
        throw new Error(`staypoints API failed: ${staypointsResponse.status}`);
      }
      if (!triplegsResponse.ok) {
        throw new Error(`triplegs API failed: ${triplegsResponse.status}`);
      }
      if (!tripsResponse.ok) {
        throw new Error(`trips API failed: ${tripsResponse.status}`);
      }

      const [locationsPayload, staypointsPayload, triplegsPayload, tripsPayload] = await Promise.all([
        locationsResponse.json(),
        staypointsResponse.json(),
        triplegsResponse.json(),
        tripsResponse.json(),
      ]);
      const allLocations = locationsPayload as DerivedLocation[];

      const locationIdsInRange = new Set<string>(
        (staypointsPayload as DerivedStaypoint[])
          .map((staypoint) => staypoint.effective_location_id)
          .filter((value): value is string => typeof value === 'string' && value.length > 0)
      );
      const filteredLocations = allLocations.filter((location) => locationIdsInRange.has(location.id));

      setLocations(allLocations);
      setVisibleLocations(filteredLocations);
      setStaypoints(staypointsPayload);
      setTriplegs(triplegsPayload);
      setTrips(tripsPayload);
      setPoiCandidatesByLocationId(new Map());
    },
    [rawGnssFetchRange.endIso, rawGnssFetchRange.startIso]
  );

  const loadPlaygroundData = useCallback(async () => {
    if (!selectedDevice) {
      setRawPoints([]);
      setSelectedRawPointId(null);
      setSelectedDerivedTarget(null);
      setLocations([]);
      setVisibleLocations([]);
      setStaypoints([]);
      setTriplegs([]);
      setTrips([]);
      setPoiCandidatesByLocationId(new Map());
      setRebuildJob(null);
      return;
    }
    setLoading(true);
    try {
      const token = await fetchToken();
      const [rawPayload] = await Promise.all([
        fetchAllRawGnss(token, selectedDevice.id),
        fetchDerived(token, selectedDevice.id),
      ]);
      setRawPoints(rawPayload);
      setSelectedRawPointId((current) => (rawPayload.some((point) => point.id === current) ? current : null));
    } catch (error) {
      secureLogger.error('Failed to load location analysis playground data', {
        error: error instanceof Error ? error.message : 'unknown',
      });
      message.error(error instanceof Error ? error.message : 'playground データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [fetchAllRawGnss, fetchDerived, fetchToken, selectedDevice]);

  useEffect(() => {
    void loadPlaygroundData();
  }, [loadPlaygroundData]);

  useEffect(() => {
    setRebuildJob(null);
  }, [dayRange.endIso, dayRange.startIso, selectedDevice?.id]);

  const handleRunAnalysis = useCallback(async (parameters: TrackintelRunParameters) => {
    if (!selectedDevice) {
      return;
    }
    setRunning(true);
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/rebuild`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          device_id: selectedDevice.id,
          range_start: rawGnssFetchRange.startIso,
          range_end: rawGnssFetchRange.endIso,
          include_debug_positionfixes: true,
          trackintel_dist_threshold_m: parameters.distThresholdM,
          trackintel_time_threshold_min: parameters.timeThresholdMin,
          trackintel_gap_threshold_min: parameters.gapThresholdMin,
          trackintel_merge_staypoints_enabled: parameters.mergeStaypointsEnabled,
          trackintel_merge_staypoints_max_time_gap: `${parameters.mergeStaypointsMaxTimeGapMin}min`,
        }),
      });
      if (!response.ok) {
        throw new Error(`rebuild API failed: ${response.status}`);
      }
      const payload = (await response.json()) as RebuildJob;
      setRebuildJob(payload);
      message.success('LocationAnalysis job を受け付けたよ');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'LocationAnalysis 実行に失敗しました');
    } finally {
      setRunning(false);
    }
  }, [
    fetchToken,
    rawGnssFetchRange.endIso,
    rawGnssFetchRange.startIso,
    selectedDevice,
  ]);

  const handleRenameLocation = useCallback(async (locationId: string, label: string) => {
    const normalizedLabel = label.trim();
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/locations/${locationId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          label: normalizedLabel || null,
          note: 'playground timeline location label edit',
        }),
      });
      if (!response.ok) {
        throw new Error(`location update API failed: ${response.status}`);
      }
      const updatedLocation = (await response.json()) as DerivedLocation;
      setLocations((current) => {
        if (current.some((location) => location.id === updatedLocation.id)) {
          return current.map((location) =>
            location.id === updatedLocation.id ? { ...location, ...updatedLocation } : location
          );
        }
        return [...current, updatedLocation];
      });
      setVisibleLocations((current) =>
        current.map((location) => (location.id === updatedLocation.id ? { ...location, ...updatedLocation } : location))
      );
      message.success('Location 名を更新しました');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Location 名の更新に失敗しました');
      throw error;
    }
  }, [fetchToken]);

  const handleUpdateLocationSemanticType = useCallback(async (locationId: string, semanticType: string) => {
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/locations/${locationId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          semantic_type: semanticType,
          note: 'playground timeline location semantic type edit',
        }),
      });
      if (!response.ok) {
        throw new Error(`location semantic update API failed: ${response.status}`);
      }
      const updatedLocation = (await response.json()) as DerivedLocation;
      setLocations((current) =>
        current.map((location) => (location.id === updatedLocation.id ? { ...location, ...updatedLocation } : location))
      );
      setVisibleLocations((current) =>
        current.map((location) => (location.id === updatedLocation.id ? { ...location, ...updatedLocation } : location))
      );
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Location カテゴリの更新に失敗しました');
      throw error;
    }
  }, [fetchToken]);

  const handleSearchLocationPoi = useCallback(async (
    locationId: string,
    query: string,
    options?: { silent?: boolean }
  ) => {
    const normalizedQuery = query.trim();
    const cacheKey = `${locationId}\u0000${normalizedQuery}`;
    const cachedCandidates = poiSearchCacheRef.current.get(cacheKey);
    if (cachedCandidates) {
      setPoiCandidatesByLocationId((current) => {
        const next = new Map(current);
        next.set(locationId, cachedCandidates);
        return next;
      });
      if (!options?.silent) {
        message.success(`POI候補を ${cachedCandidates.length} 件取得しました`);
      }
      return;
    }
    const requestId = (poiSearchRequestIdByLocationRef.current.get(locationId) || 0) + 1;
    poiSearchRequestIdByLocationRef.current.set(locationId, requestId);
    setSearchingPoiLocationId(locationId);
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/locations/${locationId}/poi-candidates`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: normalizedQuery || null,
          radius_m: 300,
          language_code: 'ja',
          max_result_count: 8,
        }),
      });
      if (!response.ok) {
        throw new Error(`POI candidates API failed: ${response.status}`);
      }
      const candidates = (await response.json()) as POICandidate[];
      poiSearchCacheRef.current.set(cacheKey, candidates);
      if (poiSearchRequestIdByLocationRef.current.get(locationId) !== requestId) {
        return;
      }
      setPoiCandidatesByLocationId((current) => {
        const next = new Map(current);
        next.set(locationId, candidates);
        return next;
      });
      if (!options?.silent) {
        message.success(`POI候補を ${candidates.length} 件取得しました`);
      }
    } catch (error) {
      if (options?.silent) {
        secureLogger.warn('Incremental POI search failed', {
          locationId,
          reason: error instanceof Error ? error.message : 'unknown',
        });
      } else {
        message.error(error instanceof Error ? error.message : 'POI候補検索に失敗しました');
      }
      throw error;
    } finally {
      if (poiSearchRequestIdByLocationRef.current.get(locationId) === requestId) {
        setSearchingPoiLocationId(null);
      }
    }
  }, [fetchToken]);

  const handleLinkLocationPoi = useCallback(async (locationId: string, candidate: POICandidate) => {
    setLinkingPoiLocationId(locationId);
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/locations/${locationId}/poi-link`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          candidate,
          note: 'playground manual Google POI link',
        }),
      });
      if (!response.ok) {
        throw new Error(`POI link API failed: ${response.status}`);
      }
      const poi = (await response.json()) as POIEnrichment;
      setLocations((current) =>
        current.map((location) =>
          location.id === locationId
            ? { ...location, label: candidate.display_name || location.label, primary_poi: poi }
            : location
        )
      );
      setVisibleLocations((current) =>
        current.map((location) =>
          location.id === locationId
            ? { ...location, label: candidate.display_name || location.label, primary_poi: poi }
            : location
        )
      );
      setPoiCandidatesByLocationId((current) => {
        const next = new Map(current);
        next.delete(locationId);
        return next;
      });
      message.success('Google POI を紐付けました');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Google POI の紐付けに失敗しました');
      throw error;
    } finally {
      setLinkingPoiLocationId(null);
    }
  }, [fetchToken]);

  const handleReassignStaypointLocation = useCallback(async (
    staypointId: string,
    payload:
      | { action: 'assign'; locationId: string }
      | { action: 'create_location'; label: string | null; semanticType: string }
      | { action: 'clear' }
  ) => {
    const body =
      payload.action === 'assign'
        ? {
            staypoint_ids: [staypointId],
            action: 'assign',
            location_id: payload.locationId,
            note: 'playground timeline staypoint location reassignment',
          }
        : payload.action === 'create_location'
          ? {
              staypoint_ids: [staypointId],
              action: 'create_location',
              create_location: {
                label: payload.label,
                semantic_type: payload.semanticType,
              },
              note: 'playground timeline staypoint location split',
            }
          : {
              staypoint_ids: [staypointId],
              action: 'clear',
              note: 'playground timeline staypoint location override clear',
            };
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/staypoints/reassign`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error(`staypoint reassign API failed: ${response.status}`);
      }
      await loadPlaygroundData();
      message.success('Staypoint のロケーション割当を更新しました');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Staypoint のロケーション割当更新に失敗しました');
      throw error;
    }
  }, [fetchToken, loadPlaygroundData]);

  const handleOverrideTriplegMode = useCallback(async (triplegId: string, mode: string) => {
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/triplegs/${triplegId}/mode`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mode,
          note: 'playground timeline tripleg mode edit',
        }),
      });
      if (!response.ok) {
        throw new Error(`tripleg mode API failed: ${response.status}`);
      }
      await loadPlaygroundData();
      message.success('Tripleg の移動手段を更新しました');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Tripleg の移動手段更新に失敗しました');
      throw error;
    }
  }, [fetchToken, loadPlaygroundData]);

  const handleDeleteTripleg = useCallback(async (triplegId: string) => {
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/triplegs/${triplegId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`tripleg delete API failed: ${response.status}`);
      }
      setSelectedDerivedTarget((current) =>
        current?.type === 'tripleg' && current.id === triplegId ? null : current
      );
      await loadPlaygroundData();
      message.success('Tripleg を削除しました');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Tripleg の削除に失敗しました');
      throw error;
    }
  }, [fetchToken, loadPlaygroundData]);

  useEffect(() => {
    if (!rebuildJob || !['accepted', 'running'].includes(rebuildJob.status)) {
      return undefined;
    }

    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const token = await fetchToken();
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/rebuild-jobs/${rebuildJob.job_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`rebuild job API failed: ${response.status}`);
        }
        const payload = (await response.json()) as RebuildJob;
        if (cancelled) {
          return;
        }
        setRebuildJob(payload);
        if (payload.status === 'succeeded') {
          await loadPlaygroundData();
          message.success('LocationAnalysis が終わったよ');
        }
        if (payload.status === 'failed') {
          message.error(payload.error_message || 'LocationAnalysis job が失敗しました');
        }
      } catch (error) {
        if (!cancelled) {
          secureLogger.error('Failed to poll location analysis rebuild job', {
            error: error instanceof Error ? error.message : 'unknown',
          });
        }
      }
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [fetchToken, loadPlaygroundData, rebuildJob]);

  const locationNameMap = useMemo(
    () =>
      new Map(
        locations.map((location) => [location.id, location.label?.trim() || `Location ${location.id.slice(0, 8)}`])
      ),
    [locations]
  );

  const locationPoiMap = useMemo(
    () =>
      new Map(
        locations
          .filter((location): location is DerivedLocation & { primary_poi: POIEnrichment } => !!location.primary_poi)
          .map((location) => [location.id, location.primary_poi])
      ),
    [locations]
  );

  const staypointNameMap = useMemo(
    () => new Map(staypoints.map((staypoint, index) => [staypoint.id, `SP ${index + 1}`])),
    [staypoints]
  );

  const tripNameMap = useMemo(
    () => new Map(trips.map((trip, index) => [trip.id, `Trip ${index + 1}`])),
    [trips]
  );

  const timelineItems = useMemo(
    () => buildLocationAnalysisTimelineItems(staypoints, triplegs),
    [staypoints, triplegs]
  );

  useEffect(() => {
    if (!selectedDerivedTarget) {
      return;
    }
    const targetExists =
      selectedDerivedTarget.type === 'staypoint'
        ? staypoints.some((staypoint) => staypoint.id === selectedDerivedTarget.id)
        : triplegs.some((tripleg) => tripleg.id === selectedDerivedTarget.id);
    if (!targetExists) {
      setSelectedDerivedTarget(null);
    }
  }, [selectedDerivedTarget, staypoints, triplegs]);

  const interpolatedPoints = useMemo<RawGnssPoint[]>(() => {
    if (!selectedDevice || !rebuildJob?.result?.stages) {
      return [];
    }
    const trackintelStage = rebuildJob.result.stages.find((stage) => stage.name === 'trackintel_analyze');
    const deviceRows = Array.isArray(trackintelStage?.devices)
      ? (trackintelStage.devices as TrackintelStageDeviceRow[])
      : [];
    const selectedDeviceStage = deviceRows.find((row) => row.device_id === selectedDevice.id);
    const positionfixes = Array.isArray(selectedDeviceStage?.positionfixes)
      ? selectedDeviceStage.positionfixes
      : [];
    return positionfixes.filter(
      (point) =>
        point.is_interpolated &&
        isWithinRange(point.measured_at, rawGnssFetchRange.startIso, rawGnssFetchRange.endIso)
    );
  }, [rawGnssFetchRange.endIso, rawGnssFetchRange.startIso, rebuildJob?.result?.stages, selectedDevice]);

  const excludedPointReasonById = useMemo(() => {
    const reasonById = new Map<string, string>();
    if (!selectedDevice || !rebuildJob?.result?.stages) {
      return reasonById;
    }
    const trackintelStage = rebuildJob.result.stages.find((stage) => stage.name === 'trackintel_analyze');
    const deviceRows = Array.isArray(trackintelStage?.devices)
      ? (trackintelStage.devices as TrackintelStageDeviceRow[])
      : [];
    const selectedDeviceStage = deviceRows.find((row) => row.device_id === selectedDevice.id);
    const excludedPositionfixes = Array.isArray(selectedDeviceStage?.excluded_positionfixes)
      ? selectedDeviceStage.excluded_positionfixes
      : [];
    excludedPositionfixes.forEach((point) => {
      if (isWithinRange(point.measured_at, rawGnssFetchRange.startIso, rawGnssFetchRange.endIso)) {
        reasonById.set(point.snapshot_id, point.exclusion_reason);
      }
    });
    return reasonById;
  }, [rawGnssFetchRange.endIso, rawGnssFetchRange.startIso, rebuildJob?.result?.stages, selectedDevice]);

  const rawGnssDisplayPoints = useMemo<RawGnssPoint[]>(
    () => [
      ...rawPoints.map((point) => ({
        ...point,
        analysis_exclusion_reason: excludedPointReasonById.get(point.id) ?? null,
      })),
      ...interpolatedPoints,
    ],
    [excludedPointReasonById, interpolatedPoints, rawPoints]
  );

  const rawGnssTableRows = useMemo<RawGnssTableRow[]>(() => {
    const sortedPoints = rawGnssDisplayPoints
      .slice()
      .sort((left, right) => {
        const leftTime = parseMeasuredAt(left.measured_at)?.valueOf() ?? 0;
        const rightTime = parseMeasuredAt(right.measured_at)?.valueOf() ?? 0;
        return leftTime - rightTime;
      });

    return sortedPoints.map((point, index) => {
      if (index === 0) {
        return {
          ...point,
          row_number: index + 1,
          measured_at_label: formatDateTime(point.measured_at),
          previous_distance_m: null,
          previous_time_diff_seconds: null,
          previous_speed_kmh: null,
        };
      }

      const previousPoint = sortedPoints[index - 1];
      const previousTime = parseMeasuredAt(previousPoint.measured_at);
      const currentTime = parseMeasuredAt(point.measured_at);
      const timeDiffSeconds =
        previousTime && currentTime ? (currentTime.getTime() - previousTime.getTime()) / 1000 : null;
      const distanceM = haversineMeters(
        previousPoint.latitude,
        previousPoint.longitude,
        point.latitude,
        point.longitude
      );
      const speedKmh =
        typeof timeDiffSeconds === 'number' && timeDiffSeconds > 0
          ? (distanceM / timeDiffSeconds) * 3.6
          : null;

      return {
        ...point,
        row_number: index + 1,
        measured_at_label: formatDateTime(point.measured_at),
        previous_distance_m: distanceM,
        previous_time_diff_seconds:
          typeof timeDiffSeconds === 'number' && timeDiffSeconds > 0 ? timeDiffSeconds : null,
        previous_speed_kmh: speedKmh,
      };
    });
  }, [rawGnssDisplayPoints]);

  const stageRows = rebuildJob?.result?.stages || [];

  const staypointColumns = useMemo<ColumnsType<DerivedStaypoint>>(
    () => [
      {
        title: '#',
        key: 'display_index',
        width: 64,
        render: (_value, _record, index) => index + 1,
      },
      {
        title: '種別',
        dataIndex: 'is_activity',
        key: 'is_activity',
        render: (value: boolean | null | undefined) => (
          value === false ? <Tag color="orange">short</Tag> : <Tag color="green">activity</Tag>
        ),
      },
      {
        title: '開始',
        dataIndex: 'started_at',
        key: 'started_at',
        render: (value: string) => formatDateTime(value),
      },
      {
        title: '終了',
        dataIndex: 'ended_at',
        key: 'ended_at',
        render: (value: string) => formatDateTime(value),
      },
      {
        title: 'location',
        key: 'effective_location_id',
        render: (_, record) => {
          const locationId = record.effective_location_id;
          if (!locationId) {
            return <Tag>未所属</Tag>;
          }
          return <Tag color="green">{locationNameMap.get(locationId) || locationId.slice(0, 8)}</Tag>;
        },
      },
      {
        title: '点数',
        dataIndex: 'source_snapshot_count',
        key: 'source_snapshot_count',
      },
    ],
    [locationNameMap]
  );

  const triplegColumns = useMemo<ColumnsType<DerivedTripleg>>(
    () => [
      {
        title: 'trip',
        key: 'trip_id',
        render: (_, record) => {
          if (!record.trip_id) {
            return <Tag>未所属</Tag>;
          }
          return <Tag color="purple">{tripNameMap.get(record.trip_id) || record.trip_id.slice(0, 8)}</Tag>;
        },
      },
      {
        title: '開始',
        dataIndex: 'started_at',
        key: 'started_at',
        render: (value: string) => formatDateTime(value),
      },
      {
        title: '終了',
        dataIndex: 'ended_at',
        key: 'ended_at',
        render: (value: string) => formatDateTime(value),
      },
      {
        title: '推定mode',
        key: 'effective_mode',
        render: (_, record) => (
          <Space>
            <Tag color="blue" icon={<TriplegModeIcon mode={record.effective_mode} />}>
              {getTriplegModeLabel(record.effective_mode)}
            </Tag>
            {record.effective_mode !== record.inferred_mode ? (
              <Text type="secondary">auto: {record.inferred_mode}</Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: 'distance',
        dataIndex: 'matched_distance_m',
        key: 'matched_distance_m',
        render: (value?: number | null) => formatDistance(value),
      },
      {
        title: '点数',
        dataIndex: 'source_snapshot_count',
        key: 'source_snapshot_count',
      },
      {
        title: '操作',
        key: 'actions',
        render: (_, record, index) => (
          <Space>
            <Button size="small" onClick={() => {
              setSelectedDerivedTarget({ type: 'tripleg', id: record.id });
            }}>
              選択
            </Button>
            <Button
              size="small"
              danger
              onClick={() => {
                Modal.confirm({
                  title: `Tripleg ${index + 1} を削除しますか？`,
                  content: '元の自動推定結果は保持し、手動削除 override として非表示にします。',
                  okText: '削除',
                  okButtonProps: { danger: true },
                  cancelText: 'キャンセル',
                  onOk: () => handleDeleteTripleg(record.id),
                });
              }}
            >
              削除
            </Button>
          </Space>
        ),
      },
    ],
    [handleDeleteTripleg, tripNameMap]
  );

  const tripColumns = useMemo<ColumnsType<DerivedTrip>>(
    () => [
      {
        title: '#',
        key: 'display_index',
        width: 64,
        render: (_value, _record, index) => index + 1,
      },
      {
        title: '開始',
        dataIndex: 'started_at',
        key: 'started_at',
        render: (value: string) => formatDateTime(value),
      },
      {
        title: '終了',
        dataIndex: 'ended_at',
        key: 'ended_at',
        render: (value: string) => formatDateTime(value),
      },
      {
        title: 'start SP',
        key: 'start_staypoint_id',
        render: (_, record) =>
          record.start_staypoint_id ? (
            <Tag color="green">{staypointNameMap.get(record.start_staypoint_id) || record.start_staypoint_id.slice(0, 8)}</Tag>
          ) : (
            <Tag>なし</Tag>
          ),
      },
      {
        title: 'end SP',
        key: 'end_staypoint_id',
        render: (_, record) =>
          record.end_staypoint_id ? (
            <Tag color="green">{staypointNameMap.get(record.end_staypoint_id) || record.end_staypoint_id.slice(0, 8)}</Tag>
          ) : (
            <Tag>なし</Tag>
          ),
      },
      {
        title: 'triplegs',
        dataIndex: 'tripleg_count',
        key: 'tripleg_count',
      },
    ],
    [staypointNameMap]
  );

  const stageColumns = useMemo<ColumnsType<RebuildStage>>(
    () => [
      {
        title: 'stage',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'status',
        dataIndex: 'status',
        key: 'status',
        render: (value: string) => <Tag color={value === 'succeeded' ? 'green' : value === 'failed' ? 'red' : 'blue'}>{value}</Tag>,
      },
      {
        title: 'detail',
        key: 'detail',
        render: (_, record) => (
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
            {JSON.stringify(summarizeStageDetail(record), null, 2)}
          </pre>
        ),
      },
    ],
    []
  );

  const rawGnssColumns = useMemo<ColumnsType<RawGnssTableRow>>(
    () => [
      {
        title: '#',
        dataIndex: 'row_number',
        key: 'row_number',
        width: 72,
        fixed: 'left',
      },
      {
        title: '時刻',
        dataIndex: 'measured_at_label',
        key: 'measured_at_label',
        width: 180,
        render: (_: string, record) => (
          <Space size={8}>
            <span>{record.measured_at_label}</span>
            {record.is_interpolated ? <Tag color="green">補間</Tag> : null}
            {record.analysis_exclusion_reason ? <Tag color="red">外れ値</Tag> : null}
          </Space>
        ),
      },
      {
        title: '緯度',
        dataIndex: 'latitude',
        key: 'latitude',
        width: 140,
        render: (value: number) => value.toFixed(6),
      },
      {
        title: '経度',
        dataIndex: 'longitude',
        key: 'longitude',
        width: 140,
        render: (value: number) => value.toFixed(6),
      },
      {
        title: '精度',
        dataIndex: 'accuracy',
        key: 'accuracy',
        width: 120,
        render: (value?: number | null) => formatAccuracy(value),
      },
      {
        title: '高度',
        dataIndex: 'altitude',
        key: 'altitude',
        width: 120,
        render: (value?: number | null) => formatAltitude(value),
      },
      {
        title: '前点からの距離',
        dataIndex: 'previous_distance_m',
        key: 'previous_distance_m',
        width: 150,
        render: (value?: number | null) => formatDistance(value),
      },
      {
        title: '前点からの時間差',
        dataIndex: 'previous_time_diff_seconds',
        key: 'previous_time_diff_seconds',
        width: 150,
        render: (value?: number | null) => formatDurationSeconds(value),
      },
      {
        title: '測定速度',
        dataIndex: 'speed',
        key: 'speed',
        width: 120,
        render: (value?: number | null) => formatMeasuredSpeed(value),
      },
      {
        title: '推定速度',
        dataIndex: 'previous_speed_kmh',
        key: 'previous_speed_kmh',
        width: 140,
        render: (value?: number | null) => formatSpeed(value),
      },
    ],
    []
  );

  const pageHeaderControls = useMemo(() => {
    if (!selectedDevice) {
      return null;
    }
    return (
      <Space wrap>
        <DatePicker value={selectedDay} onChange={handleSelectedDayChange} allowClear={false} size="small" />
        <Button icon={<ReloadOutlined />} onClick={() => void loadPlaygroundData()} size="small">
          再読込
        </Button>
        <TrackintelControls
          running={running || ['accepted', 'running'].includes(rebuildJob?.status || '')}
          onRun={handleRunAnalysis}
        />
      </Space>
    );
  }, [handleRunAnalysis, handleSelectedDayChange, loadPlaygroundData, rebuildJob?.status, running, selectedDay, selectedDevice]);

  useEffect(() => {
    setPageHeader({
      title: 'Location Analysis Playground',
      controls: pageHeaderControls,
    });
  }, [pageHeaderControls, setPageHeader]);

  useEffect(() => {
    return () => setPageHeader(null);
  }, [setPageHeader]);

  if (!selectedDevice) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="デバイスを選択してください" />;
  }

  const locationExtentBufferM = rebuildJob?.trackintel_dist_threshold_m ?? DEFAULT_TRACKINTEL_DIST_THRESHOLD_M;

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <style>
          {`
            .location-analysis-raw-row-interpolated > td {
              background: #f6ffed !important;
            }
            .location-analysis-raw-row-interpolated.ant-table-row-selected > td {
              background: #d9f7be !important;
            }
            .location-analysis-raw-row-excluded > td {
              background: #fff1f0 !important;
            }
            .location-analysis-raw-row-excluded.ant-table-row-selected > td {
              background: #ffccc7 !important;
            }
          `}
        </style>
        {rebuildJob && ['accepted', 'running'].includes(rebuildJob.status) ? (
          <Alert
            type="info"
            showIcon
            message="解析ジョブ実行中"
            description={`job=${rebuildJob.job_id} status=${rebuildJob.status}`}
          />
        ) : null}
        {rebuildJob?.status === 'failed' ? (
          <Alert type="error" showIcon message="解析ジョブ失敗" description={rebuildJob.error_message || '-'} />
        ) : null}

        <Row gutter={16}>
          <Col xs={24} md={6}>
            <Card>
              <Statistic title="Raw GNSS points" value={rawPoints.length} />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card>
              <Statistic title="Staypoints" value={staypoints.length} />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card>
              <Statistic title="Triplegs" value={triplegs.length} />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card>
              <Statistic title="Trips" value={trips.length} />
            </Card>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} xl={12}>
            <Card title="Raw GNSS">
              <LocationAnalysisMap
                title="Raw GNSS"
                rawPoints={rawGnssTableRows}
                selectedRawPointId={selectedRawPointId}
                height={720}
              />
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card title="Derived staypoints / triplegs">
              <LocationAnalysisMap
                title="Derived result"
                staypoints={staypoints}
                triplegs={triplegs}
                trips={trips}
                locations={visibleLocations}
                locationExtentBufferM={locationExtentBufferM}
                selectedDerivedTarget={selectedDerivedTarget}
                height={720}
              />
            </Card>
          </Col>
        </Row>

        <Card title="タイムライン">
          <LocationAnalysisTimeline
            items={timelineItems}
            locations={locations}
            locationNameMap={locationNameMap}
            locationPoiMap={locationPoiMap}
            poiCandidatesByLocationId={poiCandidatesByLocationId}
            searchingPoiLocationId={searchingPoiLocationId}
            linkingPoiLocationId={linkingPoiLocationId}
            selectedTarget={selectedDerivedTarget}
            formatDateTime={formatDateTime}
            formatDistance={formatDistance}
            onSelect={(target) => setSelectedDerivedTarget(target)}
            onRenameLocation={handleRenameLocation}
            onUpdateLocationSemanticType={handleUpdateLocationSemanticType}
            onOverrideTriplegMode={handleOverrideTriplegMode}
            onDeleteTripleg={handleDeleteTripleg}
            onReassignStaypointLocation={handleReassignStaypointLocation}
            onSearchLocationPoi={handleSearchLocationPoi}
            onLinkLocationPoi={handleLinkLocationPoi}
          />
        </Card>

        <Title level={4} style={{ margin: 0 }}>
          解析詳細
        </Title>

        <Row gutter={16}>
          <Col xs={24} lg={8}>
            <Card title="Locations">
              <Table<DerivedLocation>
                rowKey="id"
                size="small"
                pagination={{ pageSize: 10, showSizeChanger: false }}
                dataSource={visibleLocations}
                columns={[
                  {
                    title: 'label',
                    key: 'label',
                    render: (_, record) => record.label?.trim() || `Location ${record.id.slice(0, 8)}`,
                  },
                  {
                    title: 'type',
                    dataIndex: 'semantic_type',
                    key: 'semantic_type',
                    render: (value: string) => (
                      <Tag color="green" icon={<SemanticTypeIcon semanticType={value} />}>
                        {getSemanticTypeLabel(value)}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Google POI',
                    key: 'primary_poi',
                    render: (_, record) =>
                      record.primary_poi ? (
                        <Tag color="geekblue">{record.primary_poi.display_name || record.primary_poi.provider_place_id}</Tag>
                      ) : (
                        <Tag>未同定</Tag>
                      ),
                  },
                  {
                    title: 'visits',
                    dataIndex: 'visit_count',
                    key: 'visit_count',
                  },
                  {
                    title: '観測半径',
                    key: 'observed_radius_m',
                    render: (_, record) => formatObservedExtentDistance(record.radius_m, locationExtentBufferM),
                  },
                  {
                    title: 'extent半径',
                    dataIndex: 'radius_m',
                    key: 'radius_m',
                    render: (value?: number | null) => formatDistance(value),
                  },
                ]}
              />
            </Card>
          </Col>
          <Col xs={24} lg={16}>
            <Card title="Rebuild result">
              {rebuildJob?.result ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Space wrap>
                    <Tag>candidate stays: {rebuildJob.result.candidate_stays ?? 0}</Tag>
                    <Tag>candidate triplegs: {rebuildJob.result.candidate_triplegs ?? 0}</Tag>
                    <Tag>candidate trips: {rebuildJob.result.candidate_trips ?? 0}</Tag>
                    <Tag color={(rebuildJob.result.filtered_outlier_count ?? 0) > 0 ? 'red' : undefined}>
                      filtered outliers: {rebuildJob.result.filtered_outlier_count ?? 0}
                    </Tag>
                    <Tag>created staypoints: {rebuildJob.result.created_staypoints}</Tag>
                    <Tag>created triplegs: {rebuildJob.result.created_triplegs ?? 0}</Tag>
                    <Tag>created trips: {rebuildJob.result.created_trips ?? 0}</Tag>
                    <Tag>matched by Valhalla: {rebuildJob.result.valhalla_matched_triplegs ?? 0}</Tag>
                    <Tag>dist: {rebuildJob.trackintel_dist_threshold_m ?? DEFAULT_TRACKINTEL_DIST_THRESHOLD_M}m</Tag>
                    <Tag>time: {rebuildJob.trackintel_time_threshold_min ?? DEFAULT_TRACKINTEL_TIME_THRESHOLD_MIN}min</Tag>
                    <Tag>gap: {rebuildJob.trackintel_gap_threshold_min ?? DEFAULT_TRACKINTEL_GAP_THRESHOLD_MIN}min</Tag>
                    <Tag>merge: {(rebuildJob.trackintel_merge_staypoints_enabled ?? DEFAULT_TRACKINTEL_MERGE_STAYPOINTS_ENABLED) ? 'on' : 'off'}</Tag>
                    <Tag>
                      merge gap: {rebuildJob.trackintel_merge_staypoints_max_time_gap ?? `${DEFAULT_TRACKINTEL_MERGE_STAYPOINTS_MAX_TIME_GAP_MIN}min`}
                    </Tag>
                  </Space>
                  <Table<RebuildStage>
                    rowKey={(record) => record.name}
                    size="small"
                    pagination={false}
                    dataSource={stageRows}
                    columns={stageColumns}
                  />
                </Space>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="まだ解析結果がない" />
              )}
            </Card>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} xl={8}>
            <Card title="Staypoints">
              <Table<DerivedStaypoint>
                rowKey="id"
                size="small"
                dataSource={staypoints}
                columns={staypointColumns}
                pagination={{ pageSize: 10, showSizeChanger: false }}
              />
            </Card>
          </Col>
          <Col xs={24} xl={8}>
            <Card title="Trips">
              <Table<DerivedTrip>
                rowKey="id"
                size="small"
                dataSource={trips}
                columns={tripColumns}
                pagination={{ pageSize: 10, showSizeChanger: false }}
              />
            </Card>
          </Col>
          <Col xs={24} xl={8}>
            <Card title="Triplegs">
              <Table<DerivedTripleg>
                rowKey="id"
                size="small"
                dataSource={triplegs}
                columns={triplegColumns}
                pagination={{ pageSize: 10, showSizeChanger: false }}
              />
            </Card>
          </Col>
        </Row>

        <Card title="Raw GNSS points detail">
          <Table<RawGnssTableRow>
            rowKey="id"
            size="small"
            dataSource={rawGnssTableRows}
            columns={rawGnssColumns}
            pagination={false}
            scroll={{ x: 1100 }}
            onRow={(record) => ({
              onClick: () => setSelectedRawPointId(record.id),
            })}
            rowClassName={(record) => {
              const classNames: string[] = [];
              if (record.id === selectedRawPointId) {
                classNames.push('ant-table-row-selected');
              }
              if (record.is_interpolated) {
                classNames.push('location-analysis-raw-row-interpolated');
              }
              if (record.analysis_exclusion_reason) {
                classNames.push('location-analysis-raw-row-excluded');
              }
              return classNames.join(' ');
            }}
          />
        </Card>
      </Space>
    </Spin>
  );
};

export default LocationAnalysisPlaygroundPage;
